import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { FloatingField } from '@/components/ui/floating-field';
import { Combobox } from '@/components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { formatCurrency } from '@/lib/utils';
import { api } from '@/lib/api';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';

const EXPENSE_TYPES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'tolls', label: 'Tolls' },
  { value: 'lumper', label: 'Lumper' },
  { value: 'parking', label: 'Parking' },
  { value: 'other', label: 'Other' },
];

export default function TripsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ loadId: '', driverId: '', vehicleId: '' });
  const [editForm, setEditForm] = useState({ status: '', revenue: '', miles: '' });
  const [expenseForm, setExpenseForm] = useState({ type: 'fuel', amount: '', note: '' });

  const tripsQ = useQuery({
    queryKey: ['trips'],
    queryFn: async () => {
      const r = await api('/api/v1/trips');
      if (r.status !== 200) throw new Error('trips');
      return r.data.trips ?? [];
    },
  });

  const loadsQ = useQuery({
    queryKey: ['loads'],
    queryFn: async () => {
      const r = await api('/api/v1/loads');
      if (r.status !== 200) throw new Error('loads');
      return r.data.loads ?? [];
    },
  });

  const driversQ = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const r = await api('/api/v1/drivers');
      if (r.status !== 200) throw new Error('drivers');
      return r.data.drivers ?? [];
    },
  });

  const vehiclesQ = useQuery({
    queryKey: ['fleet-vehicles'],
    queryFn: async () => {
      const r = await api('/api/v1/fleet/vehicles');
      if (r.status !== 200) throw new Error('vehicles');
      return r.data.vehicles ?? [];
    },
  });

  const trips = tripsQ.data ?? [];
  const loads = loadsQ.data ?? [];
  const drivers = driversQ.data ?? [];
  const vehicles = vehiclesQ.data ?? [];

  const selectedLoad = loads.find((l) => l.id === form.loadId);
  const loadHasFullAssign = Boolean(selectedLoad?.driverId && selectedLoad?.vehicleId);

  const onSelectLoad = (loadId) => {
    const load = loads.find((l) => l.id === loadId);
    setForm({
      loadId,
      driverId: load?.driverId ?? '',
      vehicleId: load?.vehicleId ?? '',
    });
    if (load && (!load.driverId || !load.vehicleId)) {
      toast.error('Assign driver and vehicle on Dispatch first');
    }
  };

  const selected = trips.find((t) => t.id === editingId);
  // Hydrate edit fields only when opening a trip — not when expense/cache
  // refreshes `selected` (that was wiping unsaved status/revenue/miles).
  const editHydratedFor = useRef(null);

  useEffect(() => {
    if (!editingId) {
      editHydratedFor.current = null;
      return;
    }
    if (!selected) return;
    if (editHydratedFor.current === editingId) return;
    editHydratedFor.current = editingId;
    setEditForm({
      status: selected.status || 'pending',
      revenue: selected.revenue != null ? String(selected.revenue) : '',
      miles: selected.milesDriven != null ? String(selected.milesDriven) : '',
    });
  }, [editingId, selected]);

  const createMut = useMutation({
    mutationFn: async (body) => {
      const r = await api('/api/v1/trips', { method: 'POST', body });
      if (r.status !== 201) throw new Error(String(r.data?.error ?? 'Create failed'));
      return r.data;
    },
    onSuccess: (data) => {
      toast.success('Trip created');
      setForm({ loadId: '', driverId: '', vehicleId: '' });
      setShowCreate(false);
      // Merge into cache — blind invalidate can refetch empty on cold CF instances
      // and make the trips list look deleted.
      if (data?.trip) {
        qc.setQueryData(['trips'], (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((t) => t.id === data.trip.id)) {
            return list.map((t) => (t.id === data.trip.id ? data.trip : t));
          }
          return [data.trip, ...list];
        });
      }
    },
    onError: (err) => toast.error(err.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => {
      const r = await api(`/api/v1/trips/${id}`, { method: 'PATCH', body });
      if (r.status !== 200) throw new Error(String(r.data?.error ?? 'Update failed'));
      return r.data;
    },
    onSuccess: (data) => {
      toast.success('Trip updated');
      if (data?.trip) {
        qc.setQueryData(['trips'], (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((t) => (t.id === data.trip.id ? { ...t, ...data.trip } : t));
        });
      }
      setEditingId(null);
      editHydratedFor.current = null;
    },
    onError: (err) => toast.error(err.message || 'Update failed'),
  });

  const expenseMut = useMutation({
    mutationFn: async ({ id, body }) => {
      const r = await api(`/api/v1/trips/${id}/expenses`, { method: 'POST', body });
      if (r.status !== 200) {
        throw new Error(String(r.data?.error ?? 'Expense failed'));
      }
      return r.data;
    },
    onSuccess: (data, vars) => {
      toast.success('Expense added');
      setExpenseForm({ type: 'fuel', amount: '', note: '' });
      if (data?.trip) {
        qc.setQueryData(['trips'], (prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((t) => (t.id === vars.id ? data.trip : t));
        });
      }
    },
    onError: (err) => toast.error(err.message || 'Expense failed'),
  });

  const handleCreate = () => {
    if (!form.loadId) {
      toast.error('Select a load');
      return;
    }
    if (!form.driverId || !form.vehicleId) {
      toast.error('Assign driver and vehicle on Dispatch first');
      return;
    }
    createMut.mutate({ loadId: form.loadId });
  };

  const saveEdit = () => {
    if (!editingId) return;
    updateMut.mutate({
      id: editingId,
      body: {
        status: editForm.status,
        revenue: editForm.revenue ? Number(editForm.revenue) : undefined,
        milesDriven: editForm.miles ? Number(editForm.miles) : undefined,
      },
    });
  };

  const addExpense = () => {
    if (!editingId) return;
    const amount = Number(expenseForm.amount);
    if (!expenseForm.type || !Number.isFinite(amount)) {
      toast.error('Type and amount required');
      return;
    }
    expenseMut.mutate({
      id: editingId,
      body: {
        type: expenseForm.type,
        amount,
        note: expenseForm.note || undefined,
      },
    });
  };

  const loadLabel = (id) => loads.find((l) => l.id === id)?.loadNumber ?? id?.slice?.(0, 8) ?? '—';
  const driverLabel = (id) => drivers.find((d) => d.id === id)?.name ?? id?.slice?.(0, 8) ?? '—';
  const vehicleLabel = (id) =>
    vehicles.find((v) => v.id === id)?.unitNumber ?? id?.slice?.(0, 8) ?? '—';

  const tableRows = trips.map((t) => ({
    ...t,
    load: loadLabel(t.loadId),
    driver: driverLabel(t.driverId),
    vehicle: vehicleLabel(t.vehicleId),
    miles: t.milesDriven ?? 0,
  }));

  const columns = [
    { key: 'load', label: 'Load', sortable: true, className: 'font-mono' },
    { key: 'driver', label: 'Driver', sortable: true },
    { key: 'vehicle', label: 'Vehicle', sortable: true, className: 'font-mono' },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'revenue',
      label: 'Revenue',
      render: (v) => (
        <span className="font-mono">{v != null ? formatCurrency(Number(v)) : '—'}</span>
      ),
    },
    {
      key: 'miles',
      label: 'Miles',
      render: (v) => <span className="font-mono">{Number(v || 0).toLocaleString()}</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <Button size="sm" variant="ghost" onClick={() => setEditingId(row.id)}>
          <Icons.Pencil className="h-3 w-3 mr-1" />
          Edit
        </Button>
      ),
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Trips"
          description="Track trip progress and performance"
          icon="Route"
          metrics={[
            { label: 'Total', value: trips.length },
            {
              label: 'Active',
              value: trips.filter((t) => t.status === 'active').length,
            },
            {
              label: 'Pending',
              value: trips.filter((t) => t.status === 'pending').length,
            },
            {
              label: 'Completed',
              value: trips.filter((t) => t.status === 'completed').length,
            },
          ]}
          actions={
            <Button onClick={() => setShowCreate(!showCreate)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Trip
            </Button>
          }
        />

        {showCreate && (
          <Card>
            <CardHeader>
              <CardTitle>Create Trip</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Load *
                  </label>
                  <Combobox
                    options={loads
                      .filter((l) => l.driverId && l.vehicleId)
                      .map((l) => ({ value: l.id, label: l.loadNumber }))}
                    value={form.loadId}
                    onChange={onSelectLoad}
                    placeholder="Select dispatched load"
                  />
                  <p className="text-xs text-muted-foreground">
                    Driver and vehicle come from Dispatch assignment on the load.
                  </p>
                </div>
                {form.loadId && loadHasFullAssign ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Driver
                      </label>
                      <p className="h-10 flex items-center px-3 border rounded-md text-sm bg-muted/30">
                        {driverLabel(form.driverId)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Vehicle
                      </label>
                      <p className="h-10 flex items-center px-3 border rounded-md text-sm bg-muted/30 font-mono">
                        {vehicleLabel(form.vehicleId)}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={createMut.isPending || !loadHasFullAssign}
                >                  {createMut.isPending ? 'Creating…' : 'Create Trip'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {editingId && selected && (
          <Card>
            <CardHeader>
              <CardTitle>
                Update Trip <span className="font-mono text-sm text-muted-foreground">{editingId.slice(0, 8)}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </label>
                  <Select
                    value={editForm.status}
                    onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FloatingField
                  label="Revenue ($)"
                  type="number"
                  value={editForm.revenue}
                  onChange={(e) => setEditForm({ ...editForm, revenue: e.target.value })}
                />
                <FloatingField
                  label="Miles Driven"
                  type="number"
                  value={editForm.miles}
                  onChange={(e) => setEditForm({ ...editForm, miles: e.target.value })}
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Add Expense</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Type
                    </label>
                    <Select
                      value={expenseForm.type}
                      onValueChange={(v) => setExpenseForm({ ...expenseForm, type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPENSE_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <FloatingField
                    label="Amount ($)"
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  />
                  <FloatingField
                    label="Note"
                    value={expenseForm.note}
                    onChange={(e) => setExpenseForm({ ...expenseForm, note: e.target.value })}
                  />
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addExpense}
                    disabled={expenseMut.isPending}
                  >
                    Add Expense
                  </Button>
                </div>
                {selected.expenses?.length ? (
                  <ul className="mt-3 text-sm text-muted-foreground space-y-1">
                    {selected.expenses.map((ex, i) => (
                      <li key={i}>
                        {ex.type}: ${Number(ex.amount).toFixed(2)}
                        {ex.note ? ` — ${ex.note}` : ''}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button variant="outline" onClick={() => setEditingId(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEdit} disabled={updateMut.isPending}>
                  {updateMut.isPending ? 'Saving…' : 'Save Update'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {tripsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading trips…</p>
        ) : (
          <DataTable columns={columns} data={tableRows} emptyMessage="No trips yet" />
        )}
      </div>
);
}
